import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';

import {LinkBasic} from 'UI/components/action/LinkBasic';

import {useMenuItem} from './useMenuItem';
import {transient} from 'Application/configs/emotionTransientProps';

import type {LinkField} from 'types/payload-types';

/**
 * The props for the `MenuItem` component, which renders an interactive navigation link inside a menu.
 * This interface defines the required link configuration and click handler, as well as an optional
 * `testId` for test automation. It ensures that each menu item can be uniquely identified, styled,
 * and interacted with in both runtime and testing environments.
 */
interface ComponentProps {
    /**
     * The navigation link data containing metadata such as label, type, and destination.
     * This is passed into the `useMenuItem` hook to compute rendering attributes.
     */
    link: LinkField;
    /**
     * A callback function that is triggered when the menu item is clicked.
     * Commonly used to handle actions like closing a navigation menu after selection.
     */
    onClick?(): void;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'MenuItem'
     */
    testId?: string;
}

/**
 * A navigation menu item component that renders a styled link with active state awareness.
 * This component uses the `useMenuItem` hook to compute all required link properties, such as
 * `href`, `rel`, `target`, `isDownload`, and whether the item is currently active. It renders
 * the link inside a styled wrapper with appropriate ARIA roles for accessibility. If the item
 * matches the current route, it applies an active style via the `$isActive` prop.
 * The component also accepts an `onClick` handler for custom behavior, such as closing a menu
 * after navigation. For testing, the `testId` prop is applied as a `data-cy` attribute on the
 * wrapper element.
 *
 * @param props         The component props.
 * @param props.link    The link configuration object used to build the menu item.
 * @param props.onClick An optional callback triggered when the menu item is clicked.
 * @param props.testId  An optional identifier for end-to-end tests, applied as `data-cy`. Defaults to `'MenuItem'`.
 * @returns A JSX element rendering a single navigation menu item.
 *
 * @example
 * ```tsx
 * <MenuItem
 *   link={{ label: 'Home', linkType: 'internal', internal: '/' }}
 *   onClick={() => console.log('Menu item clicked')}
 * />
 * ```
 */
const MenuItem = ({link, onClick, testId = 'MenuItem'}: ComponentProps) => {
    const {href, isActive, isDownload, label, rel, target} = useMenuItem(link);

    return (
        <Wrapper data-cy={testId} role="none">
            <MenuLink
                $isActive={isActive}
                download={isDownload}
                href={href!}
                rel={rel}
                role="menuitem"
                target={target}
                onClick={onClick}
            >
                {label}
            </MenuLink>
        </Wrapper>
    );
};

MenuItem.displayName = 'MenuItem';

export {MenuItem};

const Wrapper = styled('li', transient)`
    height: fit-content;
    width: 100%;

    ${media('xl')} {
        height: fit-content;
        width: fit-content;
    }
`;

interface MenuLinkProps {
    $isActive: boolean;
}

const MenuLink = styled(LinkBasic, transient)<MenuLinkProps>`
    color: ${({$isActive, theme}) => ($isActive ? theme.colors.textPrimary : theme.colors.textSecondary)};
    display: block;
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 1.4rem;
    font-weight: 500;
    line-height: 1.4;
    padding: ${spacing(4)} ${spacing(4)};
    text-align: center;
    text-decoration: none;
    transition: color 0.2s ease-in-out, outline 0.2s ease-in-out;

    &:hover, &:active {
        color: ${({theme}) => theme.colors.textPrimary};
    }

    &:focus-visible {
        color: ${({theme}) => theme.colors.textPrimary};
    }
`;